import { useState, useId } from 'react';
import { Info } from 'lucide-react';

/**
 * Small, premium tooltip.
 * - Hover on desktop, tap on mobile
 * - Navy background with white text, arrow caret, 220ms fade
 * - <InfoTooltip content="…">  ← icon-triggered variant
 * - <Tooltip content="…"><anything /></Tooltip> ← wrap-anything variant
 */
export function Tooltip({ content, children, side = 'top', className = '' }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  if (!content) return children;
  const posClass = side === 'bottom'
    ? 'top-full mt-2 left-1/2 -translate-x-1/2'
    : side === 'left'
    ? 'right-full mr-2 top-1/2 -translate-y-1/2'
    : side === 'right'
    ? 'left-full ml-2 top-1/2 -translate-y-1/2'
    : 'bottom-full mb-2 left-1/2 -translate-x-1/2';
  const caretClass = side === 'bottom'
    ? 'top-[-4px] left-1/2 -translate-x-1/2 border-r border-b'
    : side === 'left'
    ? 'right-[-4px] top-1/2 -translate-y-1/2 border-t border-r'
    : side === 'right'
    ? 'left-[-4px] top-1/2 -translate-y-1/2 border-b border-l'
    : 'bottom-[-4px] left-1/2 -translate-x-1/2 border-t border-l';
  return (
    <span
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      aria-describedby={id}
    >
      <span
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
        className="inline-flex"
      >
        {children}
      </span>
      {open && (
        <span
          id={id}
          role="tooltip"
          data-testid="tooltip-content"
          className={`pointer-events-none absolute z-40 max-w-xs w-max min-w-[8rem] px-3 py-2 rounded-xl bg-navy text-white text-[11px] leading-snug shadow-xl transition-all duration-200 opacity-100 translate-y-0 ${posClass}`}
        >
          {content}
          <span className={`absolute w-2 h-2 rotate-45 bg-navy ${caretClass}`}/>
        </span>
      )}
    </span>
  );
}

/** Icon-triggered info tooltip: <InfoTooltip content="…"/> renders a small (i) icon. */
export function InfoTooltip({ content, className = '', size = 12, testid }) {
  return (
    <Tooltip content={content} className={className}>
      <span
        data-testid={testid || 'info-tooltip'}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-navy/40 hover:text-navy cursor-help"
        aria-label="More info"
      >
        <Info size={size} strokeWidth={2}/>
      </span>
    </Tooltip>
  );
}
